'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

import ProcessTimeline from './ProcessTimeline';

import ProcessFAQ from './ProcessFAQ';
import ClientTestimonials from './ClientTestimonials';
import PreparationChecklist from './PreparationChecklist';

interface ProcessStep {
  id: number;
  title: string;
  duration: string;
  description: string;
  icon: string;
  details: string[];
}

export default function HowWeWorkContent() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [selectedPath, setSelectedPath] = useState<'standard' | 'express' | 'comprehensive'>('standard');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const processSteps: ProcessStep[] = [
  {
    id: 1,
    title: 'Online vstupní diagnóza',
    duration: '5 minut',
    description: 'Vyplníte online formulář, který nám pomůže pochopit vaši finanční situaci a priority.',
    icon: 'DocumentTextIcon',
    details: [
    'Základní informace o vaší finanční situaci',
    'Vaše cíle a priority',
    'Aktuální výzvy a otázky',
    'Preferovaný způsob komunikace']

  },
  {
    id: 2,
    title: 'První schůzka - Analýza',
    duration: '60-90 minut',
    description: 'Osobní nebo online setkání, kde společně projdeme vaši situaci a nastavíme základy spolupráce.',
    icon: 'UserGroupIcon',
    details: [
    'Detailní rozbor vaší finanční situace',
    'Identifikace priorit a cílů',
    'Návrh možných řešení',
    'Zodpovězení všech vašich otázek',
    'Nastavení dalších kroků']

  },
  {
    id: 3,
    title: 'Tvorba finančního plánu',
    duration: '1-2 týdny',
    description: 'Připravíme pro vás komplexní finanční plán přizpůsobený vašim potřebám a cílům.',
    icon: 'DocumentChartBarIcon',
    details: [
    'Analýza současného stavu',
    'Návrh konkrétních kroků',
    'Časový harmonogram',
    'Finanční projekce',
    'Alternativní scénáře']

  },
  {
    id: 4,
    title: 'Prezentace plánu',
    duration: '60 minut',
    description: 'Společně projdeme připravený plán, vysvětlíme každý krok a upravíme podle vašich připomínek.',
    icon: 'PresentationChartLineIcon',
    details: [
    'Detailní vysvětlení všech částí plánu',
    'Diskuse nad jednotlivými kroky',
    'Úpravy podle vašich připomínek',
    'Zodpovězení všech otázek',
    'Schválení finální verze']

  },
  {
    id: 5,
    title: 'Implementace',
    duration: 'Průběžně',
    description: 'Pomůžeme vám s realizací jednotlivých kroků a budeme vás provázet celým procesem.',
    icon: 'CheckCircleIcon',
    details: [
    'Asistence při vyřizování dokumentů',
    'Koordinace s finančními institucemi',
    'Pravidelné kontrolní schůzky',
    'Řešení případných komplikací',
    'Průběžné úpravy plánu']

  },
  {
    id: 6,
    title: 'Dlouhodobá spolupráce',
    duration: 'Trvale',
    description: 'Váš finanční plán roste s vaším životem. Pravidelně ho aktualizujeme a přizpůsobujeme změnám.',
    icon: 'ArrowPathIcon',
    details: [
    'Pravidelné revize plánu (minimálně 2x ročně)',
    'Reakce na životní změny',
    'Optimalizace strategie',
    'Průběžná podpora a poradenství',
    'Přístup k novým příležitostem']

  }];


  const handleStepClick = (stepId: number) => {
    if (!isHydrated) return;
    setActiveStep(activeStep === stepId ? null : stepId);
  };

  const handlePathChange = (path: 'standard' | 'express' | 'comprehensive') => {
    if (!isHydrated) return;
    setSelectedPath(path);
  };

  return (
    <main className="min-h-screen bg-background pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary to-secondary text-white py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl lg:text-5xl font-bold mb-6 font-headline">
              Jak fungujeme
            </h1>
            <p className="text-lg lg:text-xl opacity-90 mb-8">
              Transparentní proces od první schůzky až po dlouhodobé partnerství. Žádná překvapení, žádný tlak, úplná transparentnost.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/online-initial-diagnosis"
                className="px-8 py-4 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent/90 hover:shadow-hover hover:scale-102 transition-smooth inline-flex items-center justify-center space-x-2">

                <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-current" />
                <span>Získat finanční plán</span>
              </Link>
              <Link
                href="/first-meeting-analysis"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-smooth inline-flex items-center justify-center space-x-2 border border-white/20">

                <Icon name="CalendarIcon" size={20} className="text-current" />
                <span>Rezervovat schůzku</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-16 lg:py-24 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-2xl lg:text-4xl font-bold text-primary mb-4 font-headline">
              Náš proces krok za krokem
            </h2>
            <p className="text-lg text-text-secondary">
              Každý krok je navržen tak, aby vám poskytl maximální hodnotu a jasnost. Postupujeme vašim tempem.
            </p>
          </div>

          <ProcessTimeline
            steps={processSteps}
            activeStep={activeStep}
            onStepClick={handleStepClick}
            isHydrated={isHydrated} />

        </div>
      </section>

      {/* Interactive Process Navigator */}
      


















      {/* What to Expect */}
      <section className="py-16 lg:py-24 bg-trust">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl lg:text-4xl font-bold text-primary mb-12 text-center font-headline">
              Co můžete očekávat
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-card rounded-xl p-8 shadow-card">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircleIcon" size={24} className="text-success" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-primary mb-2">Co děláme</h3>
                    <ul className="space-y-2 text-text-secondary">
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckIcon" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Nasloucháme vašim potřebám a cílům</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckIcon" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Vysvětlujeme vše srozumitelně bez žargonu</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckIcon" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Vytváříme plány přizpůsobené vaší situaci</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckIcon" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Provázíme vás celým procesem implementace</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="CheckIcon" size={16} className="text-success mt-1 flex-shrink-0" />
                        <span>Pravidelně aktualizujeme váš plán</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl p-8 shadow-card">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                    <Icon name="XCircleIcon" size={24} className="text-error" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-primary mb-2">Co neděláme</h3>
                    <ul className="space-y-2 text-text-secondary">
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} className="text-error mt-1 flex-shrink-0" />
                        <span>Netlačíme na rychlá rozhodnutí</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} className="text-error mt-1 flex-shrink-0" />
                        <span>Neslibujeme nerealistické výnosy</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} className="text-error mt-1 flex-shrink-0" />
                        <span>Nepoužíváme složitý finanční žargon</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} className="text-error mt-1 flex-shrink-0" />
                        <span>Neprodáváme produkty, které nepotřebujete</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Icon name="XMarkIcon" size={16} className="text-error mt-1 flex-shrink-0" />
                        <span>Neztrácíme se po uzavření smlouvy</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preparation Checklist */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <PreparationChecklist isHydrated={isHydrated} />
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-16 lg:py-24 bg-surface">
        <div className="container mx-auto px-4">
          <ClientTestimonials isHydrated={isHydrated} />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <ProcessFAQ isHydrated={isHydrated} />
        </div>
      </section>

      {/* Timeline Expectations */}
      <section className="py-16 lg:py-24 bg-primary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl lg:text-4xl font-bold mb-8 text-center font-headline">
              Realistické časové očekávání
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-accent rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-bold text-accent-foreground">1-2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">První týdny</h3>
                    <p className="opacity-90">
                      Poznávání, analýza a tvorba základního plánu. Věnujeme čas pochopení vaší situace.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-accent rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-bold text-accent-foreground">3-6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">První měsíce</h3>
                    <p className="opacity-90">
                      Implementace základních kroků, nastavení systémů a vytvoření pevných základů.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-accent rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-bold text-accent-foreground">6+</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Dlouhodobě</h3>
                    <p className="opacity-90">
                      Pravidelné revize, optimalizace a růst vašeho finančního plánu s vaším životem.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-white/5 rounded-lg border border-white/10">
                <p className="text-sm opacity-80">
                  <strong>Důležité:</strong> Každý klient je jedinečný a časové rámce se mohou lišit. Některé kroky mohou trvat déle, jiné rychleji. Vždy postupujeme tempem, které vám vyhovuje.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl lg:text-4xl font-bold mb-6 font-headline">
              Připraveni začít?
            </h2>
            <p className="text-lg lg:text-xl opacity-90 mb-8">
              První krok je nejjednodušší - vyplňte online diagnózu nebo si rovnou rezervujte schůzku.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/online-initial-diagnosis"
                className="px-8 py-4 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent/90 hover:shadow-hover hover:scale-102 transition-smooth inline-flex items-center justify-center space-x-2">

                <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-current" />
                <span>Získat finanční plán</span>
              </Link>
              <Link
                href="/first-meeting-analysis"
                className="px-8 py-4 bg-white text-primary font-semibold rounded-lg hover:bg-white/90 transition-smooth inline-flex items-center justify-center space-x-2">

                <Icon name="CalendarIcon" size={20} className="text-current" />
                <span>Rezervovat schůzku</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brand-primary text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Co-Work Financial Partners</h3>
              <p className="text-sm opacity-80">
                Váš partner pro finanční plánování v Praze. Transparentně, lidsky, dlouhodobě.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Rychlé odkazy</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link href="/homepage" className="opacity-80 hover:opacity-100 transition-smooth">
                    Domů
                  </Link>
                </li>
                <li>
                  <Link href="/financial-plan" className="opacity-80 hover:opacity-100 transition-smooth">
                    Finanční plán
                  </Link>
                </li>
                <li>
                  <Link href="/service-long-term-partnership" className="opacity-80 hover:opacity-100 transition-smooth">
                    Dlouhodobá spolupráce
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Kontakt</h3>
              <ul className="space-y-2 text-sm opacity-80">
                <li>Praha-Chodov</li>
                <li>info@co-work.cz</li>
                <li>+420 123 456 789</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center text-sm opacity-80">
            <p>&copy; {isHydrated ? new Date().getFullYear() : '2025'} Co-Work Financial Partners. Všechna práva vyhrazena.</p>
          </div>
        </div>
      </footer>
    </main>);

}